/**
 * Timeline Store
 *
 * Manages timeline state including tracks, tools, groups, and playback for each scene tab.
 * Uses Zustand with immer middleware for immutable state updates.
 *
 * Key features:
 * - Manual track management via addTrack() and removeTrack()
 * - Auto-expands timeline duration when tools placed near edge
 * - Tracks playback state and playhead position
 * - Group creation and management for reusable tool combinations
 * - Integrates with scene store for dirty state tracking
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useSceneStore } from './sceneStore';
import { usePuppetStore, type EasingFunction } from './puppetStore';

export type BodyPart =
  | 'head'
  | 'body'
  | 'leftArm'
  | 'rightArm'
  | 'leftLeg'
  | 'rightLeg'
  | 'control';

/**
 * Target position type for Reset Position tool
 */
export type ResetTargetPosition = 'start' | 'dock';

/**
 * Tool instance placed on the timeline
 *
 * Represents a single animation action with a target rotation and easing.
 * The targetRotation specifies the absolute angle (in degrees) the body part
 * should reach by the end of the tool's duration.
 */
export interface Tool {
  id: string;
  toolId: string;
  trackNumber: number;
  startTime: number;
  duration: number;
  bodyPart: BodyPart;
  targetRotation: number; // Target angle in degrees
  easing: EasingFunction; // Easing function type
  /**
   * Target position for Reset Position tool only.
   * Determines whether to reset to starting or docking position.
   */
  targetPosition?: ResetTargetPosition;
  /**
   * ID of the puppet that owns this tool instance.
   * Used for multi-puppet support - tools from inactive puppets
   * are visually dimmed and don't animate during playback.
   */
  puppetId?: string;
}

/**
 * Input for creating a new tool on the timeline
 *
 * Same as Tool but without the auto-generated id.
 * puppetId is optional - if not provided, uses the active puppet.
 */
export interface NewToolInput {
  toolId: string;
  trackNumber: number;
  startTime: number;
  duration: number;
  bodyPart: BodyPart;
  targetRotation: number; // Target angle in degrees
  easing: EasingFunction; // Easing function type
  /** Target position for Reset Position tool only */
  targetPosition?: ResetTargetPosition;
  /** Optional puppet ID - defaults to active puppet if not specified */
  puppetId?: string;
}

export interface Track {
  number: number;
  label: string;
}

/**
 * Tool snapshot stored within a group with relative timing
 *
 * Contains all tool configuration plus relative offsets for positioning
 * when the group is unpacked onto the timeline.
 */
export interface GroupTool {
  /** Reference to the original tool definition ID */
  toolId: string;
  /** Body part this tool controls */
  bodyPart: BodyPart;
  /** Duration in seconds */
  duration: number;
  /** Target rotation angle in degrees */
  targetRotation: number;
  /** Easing function type */
  easing: EasingFunction;
  /** Time offset relative to earliest tool (earliest = 0) */
  relativeStartTime: number;
  /** Track offset relative to first track used (1-indexed) */
  relativeTrackNumber: number;
  /** Target position for Reset Position tool only */
  targetPosition?: ResetTargetPosition;
  /** ID of the puppet that owns this tool */
  puppetId?: string;
}

/**
 * Group of tools that can be placed on the timeline as a unit
 *
 * Groups store tool snapshots with relative timing so they can be
 * unpacked at any position on the timeline while preserving their
 * internal timing relationships.
 */
export interface Group {
  /** Unique identifier for the group */
  id: string;
  /** User-provided name (1-50 characters) */
  name: string;
  /** Array of tool snapshots with relative timing */
  tools: GroupTool[];
  /** Number of tracks in this group (min 1) */
  trackCount: number;
}

/**
 * Placed group instance on the timeline
 *
 * Represents a single instance of a group placed on the timeline.
 * References a Group definition by groupId - does not store tools directly.
 * Duration is calculated dynamically from the referenced Group's tools.
 */
export interface PlacedGroup {
  /** Unique identifier for this placed instance */
  id: string;
  /** Reference to the Group definition */
  groupId: string;
  /** Track number where this group is placed */
  trackNumber: number;
  /** Start time on the timeline in seconds */
  startTime: number;
  /** ID of the puppet that owns this placed group instance */
  puppetId?: string;
}

interface TimelineState {
  tracks: Track[];
  tools: Tool[];
  /** Reusable tool groups */
  groups: Group[];
  /** Placed group instances on the timeline */
  placedGroups: PlacedGroup[];
  duration: number;
  playheadPosition: number;
  isPlaying: boolean;
  playbackSpeed: number;
}

interface TimelineActions {
  addTool: (tool: NewToolInput) => void;
  removeTool: (id: string) => void;
  updateTool: (id: string, updates: Partial<Tool>) => void;
  moveTool: (id: string, trackNumber: number, startTime: number) => void;
  /**
   * Move multiple tools together, preserving relative positions
   * Used for multi-selection drag operations
   *
   * @param moves - Array of tool moves with id and new position
   */
  moveTools: (
    moves: Array<{ id: string; trackNumber: number; startTime: number }>
  ) => void;
  setPlayheadPosition: (position: number) => void;
  togglePlayback: () => void;
  stopPlayback: () => void;
  setPlaybackSpeed: (speed: number) => void;
  addTrack: () => void;
  removeTrack: (trackNumber: number) => void;
  renameTrack: (trackNumber: number, newLabel: string) => void;
  addGroupTrack: (groupId: string) => void;
  removeGroupTrack: (groupId: string) => void;
  renameGroup: (groupId: string, newName: string) => boolean;
  /**
   * Create a group from selected tools
   *
   * @param name - User-provided group name (1-50 characters, must be unique)
   * @param toolIds - Array of tool IDs to include in the group
   * @returns Group ID on success, null on validation failure
   */
  createGroup: (name: string, toolIds: string[]) => string | null;
  /**
   * Delete a group by ID
   *
   * @param id - Group ID to delete
   */
  deleteGroup: (id: string) => void;
  /**
   * Get a group by ID
   *
   * @param id - Group ID to retrieve
   * @returns Group if found, undefined otherwise
   */
  getGroupById: (id: string) => Group | undefined;
  /**
   * Place a group on the timeline
   *
   * Creates a PlacedGroup instance referencing the group definition.
   * Auto-assigns puppetId from active puppet.
   *
   * @param groupId - ID of the group definition to place
   * @param trackNumber - Track to place the group on
   * @param startTime - Start time on the timeline
   */
  addPlacedGroup: (
    groupId: string,
    trackNumber: number,
    startTime: number
  ) => void;
  /**
   * Remove a placed group from the timeline
   *
   * @param id - PlacedGroup ID to remove
   */
  removePlacedGroup: (id: string) => void;
  /**
   * Move a placed group to a new position
   *
   * @param id - PlacedGroup ID to move
   * @param trackNumber - New track number
   * @param startTime - New start time
   */
  movePlacedGroup: (id: string, trackNumber: number, startTime: number) => void;
}

type TimelineStore = TimelineState & TimelineActions;

/**
 * Maximum number of tracks allowed in the timeline
 */
const MAX_TRACKS = 20;

/**
 * Group name constraints
 */
const GROUP_NAME_MIN_LENGTH = 1;
const GROUP_NAME_MAX_LENGTH = 50;

/**
 * Generates a unique tool ID
 *
 * Uses timestamp and random string to ensure uniqueness across sessions.
 *
 * @returns Unique tool identifier string
 */
const generateToolId = (): string => {
  return `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Generates a unique group ID
 *
 * Uses timestamp and random string to ensure uniqueness across sessions.
 *
 * @returns Unique group identifier string
 */
const generateGroupId = (): string => {
  return `group-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Generates a unique placed group ID
 *
 * Uses timestamp and random string to ensure uniqueness across sessions.
 *
 * @returns Unique placed group identifier string
 */
const generatePlacedGroupId = (): string => {
  return `placed-group-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Calculate the duration of a group from its internal tools
 *
 * Duration is the maximum end time of any tool within the group,
 * calculated as max(relativeStartTime + duration) across all tools.
 *
 * @param group - Group to calculate duration for
 * @returns Duration in seconds, 0 for empty groups
 */
export const getGroupDuration = (group: Group): number => {
  if (group.tools.length === 0) {
    return 0;
  }
  return Math.max(
    ...group.tools.map((tool) => tool.relativeStartTime + tool.duration)
  );
};

/**
 * Convert GroupTools to Tool-like objects for rendering in edit mode
 *
 * Maps relative timing (relativeStartTime, relativeTrackNumber) to
 * absolute timing (startTime, trackNumber) for use with existing
 * Track/ToolBlock components.
 *
 * @param group - Group containing the tools to convert
 * @returns Array of Tool-like objects for rendering
 */
export const getGroupToolsAsTools = (group: Group): Tool[] => {
  return group.tools.map((groupTool, index) => ({
    // Generate a deterministic ID for React keys (group.id + index)
    id: `${group.id}-tool-${index}`,
    toolId: groupTool.toolId,
    // Map relative to absolute
    trackNumber: groupTool.relativeTrackNumber,
    startTime: groupTool.relativeStartTime,
    duration: groupTool.duration,
    bodyPart: groupTool.bodyPart,
    targetRotation: groupTool.targetRotation,
    easing: groupTool.easing,
    targetPosition: groupTool.targetPosition,
    puppetId: groupTool.puppetId,
  }));
};

/**
 * Auto-expand timeline duration if tool is placed near the edge
 *
 * Algorithm:
 * - If tool ends within 2 seconds of current duration, expand timeline
 * - Expansion rounds up to nearest 10-second increment for clean UI
 * - Ensures timeline always has buffer space for additional tools
 *
 * @param toolEndTime - Time when tool ends (startTime + duration)
 * @param currentDuration - Current timeline duration in seconds
 * @returns New duration if expansion needed, otherwise unchanged
 *
 * @example
 * ```typescript
 * // Tool ends at 8.5s, current duration is 10s
 * // 8.5 > (10 - 2), so expand
 * // New duration = Math.ceil((8.5 + 2) / 10) * 10 = 20
 * ```
 */
const autoExpandDuration = (
  toolEndTime: number,
  currentDuration: number
): number => {
  if (toolEndTime > currentDuration - 2) {
    return Math.ceil((toolEndTime + 2) / 10) * 10;
  }
  return currentDuration;
};

/**
 * Calculate relative offsets for tools
 *
 * Normalizes tool timing so the earliest tool starts at time 0
 * and track numbers are relative to the lowest track used (1-indexed).
 *
 * @param tools - Array of tools to calculate offsets for
 * @returns Array of GroupTool objects with relative timing
 */
export const calculateRelativeOffsets = (tools: Tool[]): GroupTool[] => {
  if (tools.length === 0) {
    return [];
  }

  // Find earliest start time and lowest track number
  const earliestTime = Math.min(...tools.map((t) => t.startTime));
  const lowestTrack = Math.min(...tools.map((t) => t.trackNumber));

  // Map tools to GroupTool with relative offsets
  return tools.map((tool) => ({
    toolId: tool.toolId,
    bodyPart: tool.bodyPart,
    duration: tool.duration,
    targetRotation: tool.targetRotation,
    easing: tool.easing,
    relativeStartTime: tool.startTime - earliestTime,
    relativeTrackNumber: tool.trackNumber - lowestTrack + 1,
    targetPosition: tool.targetPosition,
    puppetId: tool.puppetId,
  }));
};

export const useTimelineStore = create<TimelineStore>()(
  immer((set, get) => ({
    // Initial state
    tracks: [
      { number: 1, label: 'Track 1' },
      { number: 2, label: 'Track 2' },
      { number: 3, label: 'Track 3' },
    ],
    tools: [],
    groups: [],
    placedGroups: [],
    duration: 10,
    playheadPosition: 0,
    isPlaying: false,
    playbackSpeed: 1.0,

    /**
     * Add a new tool to the timeline
     *
     * Actions performed:
     * 1. Validates target track exists (returns early if not)
     * 2. Generates unique ID for the tool
     * 3. Assigns puppetId (uses active puppet if not specified)
     * 4. Adds tool to timeline
     * 5. Auto-expands duration if tool placed near edge
     * 6. Marks scene as dirty for unsaved changes tracking
     *
     * @param toolInput - Tool configuration (without ID, which is auto-generated)
     */
    addTool: (toolInput: NewToolInput) =>
      set((state) => {
        // Validate that target track exists - return early if not
        const trackExists = state.tracks.some(
          (t) => t.number === toolInput.trackNumber
        );
        if (!trackExists) {
          return;
        }

        // Get puppetId from input or fall back to active puppet
        const puppetId =
          toolInput.puppetId ?? usePuppetStore.getState().activePuppetId;

        const newTool: Tool = {
          id: generateToolId(),
          ...toolInput,
          puppetId: puppetId ?? undefined,
        };

        state.tools.push(newTool);

        // Auto-expand duration if needed
        const toolEndTime = newTool.startTime + newTool.duration;
        state.duration = autoExpandDuration(toolEndTime, state.duration);

        useSceneStore.getState().markDirty();
      }),

    /**
     * Remove a tool from the timeline
     *
     * @param id - Unique tool ID to remove
     */
    removeTool: (id: string) =>
      set((state) => {
        const toolIndex = state.tools.findIndex((tool) => tool.id === id);
        if (toolIndex !== -1) {
          state.tools.splice(toolIndex, 1);
          useSceneStore.getState().markDirty();
        }
      }),

    /**
     * Update properties of an existing tool
     *
     * Updates only the properties provided in the updates object.
     * Auto-expands timeline if duration or position changes move tool near edge.
     *
     * @param id - Unique tool ID to update
     * @param updates - Partial tool properties to update
     */
    updateTool: (id: string, updates: Partial<Tool>) =>
      set((state) => {
        const tool = state.tools.find((t) => t.id === id);
        if (tool) {
          // Update properties explicitly for immer compatibility
          if (updates.toolId !== undefined) tool.toolId = updates.toolId;
          if (updates.trackNumber !== undefined)
            tool.trackNumber = updates.trackNumber;
          if (updates.startTime !== undefined)
            tool.startTime = updates.startTime;
          if (updates.duration !== undefined) tool.duration = updates.duration;
          if (updates.bodyPart !== undefined) tool.bodyPart = updates.bodyPart;
          if (updates.targetRotation !== undefined)
            tool.targetRotation = updates.targetRotation;
          if (updates.easing !== undefined) tool.easing = updates.easing;
          if (updates.targetPosition !== undefined)
            tool.targetPosition = updates.targetPosition;
          if (updates.puppetId !== undefined) tool.puppetId = updates.puppetId;

          // Auto-expand duration if needed
          const toolEndTime = tool.startTime + tool.duration;
          state.duration = autoExpandDuration(toolEndTime, state.duration);

          useSceneStore.getState().markDirty();
        }
      }),

    /**
     * Move a tool to a different track or time position
     *
     * Validates target track exists before moving.
     * Auto-expands timeline if new position is near edge.
     *
     * @param id - Unique tool ID to move
     * @param trackNumber - Target track number
     * @param startTime - Target start time in seconds
     */
    moveTool: (id: string, trackNumber: number, startTime: number) =>
      set((state) => {
        // Validate that target track exists - return early if not
        const trackExists = state.tracks.some((t) => t.number === trackNumber);
        if (!trackExists) {
          return;
        }

        const tool = state.tools.find((t) => t.id === id);
        if (tool) {
          tool.trackNumber = trackNumber;
          tool.startTime = startTime;

          // Auto-expand duration if needed
          const toolEndTime = tool.startTime + tool.duration;
          state.duration = autoExpandDuration(toolEndTime, state.duration);

          useSceneStore.getState().markDirty();
        }
      }),

    /**
     * Move multiple tools together in a single operation
     * Preserves relative positions between tools in a selection
     */
    moveTools: (
      moves: Array<{ id: string; trackNumber: number; startTime: number }>
    ) =>
      set((state) => {
        let maxEndTime = 0;

        for (const move of moves) {
          // Validate that target track exists
          const trackExists = state.tracks.some(
            (t) => t.number === move.trackNumber
          );
          if (!trackExists) {
            continue;
          }

          const tool = state.tools.find((t) => t.id === move.id);
          if (tool) {
            tool.trackNumber = move.trackNumber;
            tool.startTime = Math.max(0, move.startTime); // Ensure non-negative
            maxEndTime = Math.max(maxEndTime, tool.startTime + tool.duration);
          }
        }

        // Auto-expand duration if any tool extends past current duration
        if (maxEndTime > 0) {
          state.duration = autoExpandDuration(maxEndTime, state.duration);
        }

        useSceneStore.getState().markDirty();
      }),

    /**
     * Set the playhead position on the timeline
     *
     * Position is clamped between 0 and timeline duration.
     *
     * @param position - Target position in seconds (0 to duration)
     */
    setPlayheadPosition: (position: number) =>
      set((state) => {
        state.playheadPosition = Math.max(
          0,
          Math.min(position, state.duration)
        );
      }),

    /**
     * Toggle playback on/off
     *
     * Used by play/pause button and Space keyboard shortcut.
     */
    togglePlayback: () =>
      set((state) => {
        state.isPlaying = !state.isPlaying;
      }),

    /**
     * Stop playback
     *
     * Sets isPlaying to false but does not reset playhead position.
     * Used by stop button.
     */
    stopPlayback: () =>
      set((state) => {
        state.isPlaying = false;
      }),

    /**
     * Set playback speed multiplier
     *
     * Speed is clamped between 0.1x (slow) and 3.0x (fast).
     *
     * @param speed - Speed multiplier (0.25, 0.5, 1.0, 2.0, or 4.0 typically)
     */
    setPlaybackSpeed: (speed: number) =>
      set((state) => {
        state.playbackSpeed = Math.max(0.1, Math.min(speed, 3.0));
      }),

    /**
     * Add a new track to the timeline
     *
     * Creates a new track with auto-generated label (Track N where N is sequential).
     * Enforces maximum of 20 tracks - returns early with warning if limit reached.
     *
     * @example
     * ```typescript
     * // With tracks [1, 2, 3] existing
     * addTrack(); // Creates Track 4
     * ```
     */
    addTrack: () =>
      set((state) => {
        // Enforce maximum track limit
        if (state.tracks.length >= MAX_TRACKS) {
          console.warn(`Maximum of ${MAX_TRACKS} tracks allowed`);
          return;
        }

        // Calculate next track number (sequential)
        const nextNumber = state.tracks.length + 1;

        state.tracks.push({
          number: nextNumber,
          label: `Track ${nextNumber}`,
        });

        useSceneStore.getState().markDirty();
      }),

    /**
     * Remove a track and all tools and placed groups on it from the timeline
     *
     * Actions performed:
     * 1. Validates track exists and is not the last track
     * 2. Removes the track from the tracks array
     * 3. Removes all tools and placed groups that were on the deleted track
     * 4. Resequences remaining tracks to maintain 1, 2, 3... order
     * 5. Updates trackNumber on affected tools and placed groups to match new track numbers
     * 6. Marks scene as dirty
     *
     * @param trackNumber - The track number to delete
     */
    removeTrack: (trackNumber: number) =>
      set((state) => {
        // Prevent deletion of last track
        if (state.tracks.length <= 1) {
          console.warn('Cannot delete the last remaining track');
          return;
        }

        // Find the track to delete
        const trackIndex = state.tracks.findIndex(
          (t) => t.number === trackNumber
        );
        if (trackIndex === -1) {
          console.warn(`Track ${trackNumber} not found`);
          return;
        }

        // Remove the track
        state.tracks.splice(trackIndex, 1);

        // Remove all tools on the deleted track
        state.tools = state.tools.filter((t) => t.trackNumber !== trackNumber);

        // Remove all placed groups on the deleted track
        state.placedGroups = state.placedGroups.filter(
          (pg) => pg.trackNumber !== trackNumber
        );

        // Build a mapping of old track numbers to new track numbers
        const trackMapping = new Map<number, number>();
        state.tracks.forEach((track, index) => {
          const oldNumber = track.number;
          const newNumber = index + 1;
          trackMapping.set(oldNumber, newNumber);
          track.number = newNumber;
          track.label = `Track ${newNumber}`;
        });

        // Update tool trackNumbers based on the mapping
        state.tools.forEach((tool) => {
          const newTrackNumber = trackMapping.get(tool.trackNumber);
          if (newTrackNumber !== undefined) {
            tool.trackNumber = newTrackNumber;
          }
        });

        // Update placed group trackNumbers based on the mapping
        state.placedGroups.forEach((pg) => {
          const newTrackNumber = trackMapping.get(pg.trackNumber);
          if (newTrackNumber !== undefined) {
            pg.trackNumber = newTrackNumber;
          }
        });

        useSceneStore.getState().markDirty();
      }),

    /**
     * Rename a track
     *
     * @param trackNumber - The track number to rename
     * @param newLabel - The new label for the track
     */
    renameTrack: (trackNumber: number, newLabel: string) =>
      set((state) => {
        const track = state.tracks.find((t) => t.number === trackNumber);
        if (track) {
          track.label = newLabel;
          useSceneStore.getState().markDirty();
        }
      }),

    addGroupTrack: (groupId: string) =>
      set((state) => {
        const group = state.groups.find((g) => g.id === groupId);
        if (!group) return;
        if (group.trackCount >= MAX_TRACKS) return;
        group.trackCount += 1;
        useSceneStore.getState().markDirty();
      }),

    removeGroupTrack: (groupId: string) =>
      set((state) => {
        const group = state.groups.find((g) => g.id === groupId);
        if (!group) return;
        if (group.trackCount <= 1) return;

        const maxUsedTrack =
          group.tools.length > 0
            ? Math.max(...group.tools.map((t) => t.relativeTrackNumber))
            : 0;

        if (group.trackCount <= maxUsedTrack) return;

        group.trackCount -= 1;
        useSceneStore.getState().markDirty();
      }),

    renameGroup: (groupId: string, newName: string): boolean => {
      const state = get();
      const trimmed = newName.trim();

      if (
        trimmed.length < GROUP_NAME_MIN_LENGTH ||
        trimmed.length > GROUP_NAME_MAX_LENGTH
      ) {
        return false;
      }

      const nameExists = state.groups.some(
        (g) => g.id !== groupId && g.name === trimmed
      );
      if (nameExists) return false;

      set((state) => {
        const group = state.groups.find((g) => g.id === groupId);
        if (group) {
          group.name = trimmed;
          useSceneStore.getState().markDirty();
        }
      });
      return true;
    },

    /**
     * Create a group from selected tools
     *
     * Creates a reusable group of tools that can be placed on the timeline.
     * Tools are stored with relative timing offsets so they maintain their
     * internal timing relationships when unpacked.
     *
     * Validation:
     * - Name must be 1-50 characters
     * - Name must be unique within the scene
     * - At least one tool ID must be provided
     *
     * @param name - User-provided group name (1-50 characters, must be unique)
     * @param toolIds - Array of tool IDs to include in the group
     * @returns Group ID on success, null on validation failure
     */
    createGroup: (name: string, toolIds: string[]) => {
      const state = get();

      // Validate name length
      if (
        name.length < GROUP_NAME_MIN_LENGTH ||
        name.length > GROUP_NAME_MAX_LENGTH
      ) {
        return null;
      }

      // Validate name uniqueness
      const nameExists = state.groups.some((g) => g.name === name);
      if (nameExists) {
        return null;
      }

      // Get the tools from the timeline
      const tools = state.tools.filter((t) => toolIds.includes(t.id));
      if (tools.length === 0) {
        return null;
      }

      // Calculate relative offsets
      const groupTools = calculateRelativeOffsets(tools);

      const trackCount =
        groupTools.length > 0
          ? Math.max(...groupTools.map((t) => t.relativeTrackNumber))
          : 1;

      // Generate ID and create group
      const groupId = generateGroupId();
      const newGroup: Group = {
        id: groupId,
        name,
        tools: groupTools,
        trackCount,
      };

      // Add group to state
      set((state) => {
        state.groups.push(newGroup);
        useSceneStore.getState().markDirty();
      });

      return groupId;
    },

    /**
     * Delete a group by ID
     *
     * @param id - Group ID to delete
     */
    deleteGroup: (id: string) =>
      set((state) => {
        const groupIndex = state.groups.findIndex((g) => g.id === id);
        if (groupIndex !== -1) {
          state.groups.splice(groupIndex, 1);
          useSceneStore.getState().markDirty();
        }
      }),

    /**
     * Get a group by ID
     *
     * @param id - Group ID to retrieve
     * @returns Group if found, undefined otherwise
     */
    getGroupById: (id: string) => {
      const state = get();
      return state.groups.find((g) => g.id === id);
    },

    /**
     * Place a group on the timeline
     *
     * Creates a PlacedGroup instance referencing the group definition.
     * Auto-assigns puppetId from active puppet.
     *
     * @param groupId - ID of the group definition to place
     * @param trackNumber - Track to place the group on
     * @param startTime - Start time on the timeline
     */
    addPlacedGroup: (groupId: string, trackNumber: number, startTime: number) =>
      set((state) => {
        // Validate that target track exists
        const trackExists = state.tracks.some((t) => t.number === trackNumber);
        if (!trackExists) {
          return;
        }

        // Get puppetId from active puppet
        const puppetId = usePuppetStore.getState().activePuppetId;

        const newPlacedGroup: PlacedGroup = {
          id: generatePlacedGroupId(),
          groupId,
          trackNumber,
          startTime,
          puppetId: puppetId ?? undefined,
        };

        state.placedGroups.push(newPlacedGroup);

        // Auto-expand duration if needed
        const group = state.groups.find((g) => g.id === groupId);
        if (group) {
          const groupDuration = getGroupDuration(group);
          const endTime = startTime + groupDuration;
          state.duration = autoExpandDuration(endTime, state.duration);
        }

        useSceneStore.getState().markDirty();
      }),

    /**
     * Remove a placed group from the timeline
     *
     * @param id - PlacedGroup ID to remove
     */
    removePlacedGroup: (id: string) =>
      set((state) => {
        const index = state.placedGroups.findIndex((pg) => pg.id === id);
        if (index !== -1) {
          state.placedGroups.splice(index, 1);
          useSceneStore.getState().markDirty();
        }
      }),

    /**
     * Move a placed group to a new position
     *
     * @param id - PlacedGroup ID to move
     * @param trackNumber - New track number
     * @param startTime - New start time
     */
    movePlacedGroup: (id: string, trackNumber: number, startTime: number) =>
      set((state) => {
        // Validate that target track exists
        const trackExists = state.tracks.some((t) => t.number === trackNumber);
        if (!trackExists) {
          return;
        }

        const placedGroup = state.placedGroups.find((pg) => pg.id === id);
        if (placedGroup) {
          placedGroup.trackNumber = trackNumber;
          placedGroup.startTime = startTime;

          // Auto-expand duration if needed
          const group = state.groups.find((g) => g.id === placedGroup.groupId);
          if (group) {
            const groupDuration = getGroupDuration(group);
            const endTime = startTime + groupDuration;
            state.duration = autoExpandDuration(endTime, state.duration);
          }

          useSceneStore.getState().markDirty();
        }
      }),
  }))
);
